import { ensureNotNil, isNil } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { ActiveFilter, HubItemIsNewFilter } from "../common";
import {
	Overview,
	dateFilterOptions,
	sortOptions
} from "./Overview/Overview";
import {
	EventDateFilterType,
	EventPreviewModel
} from "client/Api/Hub/Events/eventViewer";
import { Details } from "./Details/Details";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { HubItemViewLocation } from "client/Api/Hub/common.details";

export type EventsView = {
	kind: "overview";
} | {
	kind: "details";
	id: number;
	commentId?: number;
};

export type EventsPageContentProps = {
	view: EventsView;
	onViewChange: (view: EventsView) => void;
	selectedCategoryId: number | null;
	onActiveFilterChange: (activeFilter: ActiveFilter | null) => void;
	activeFilter: ActiveFilter | null;
	onSelectCategory: (categoryId: number | null) => void;
	categories: readonly HubItemCategoryModel[];
	onCategoryCreated: () => void;
	hubItemKindsCanCreateNewCategoriesFor: ReadonlyArray<HubItemKind>;
	canOpenHubManagement: boolean;
};

export const EventsPageContent = (props: EventsPageContentProps): ReactElement => {
	const {
		view,
		onViewChange,
		selectedCategoryId,
		onSelectCategory,
		categories,
		onCategoryCreated,
		hubItemKindsCanCreateNewCategoriesFor,
		activeFilter,
		onActiveFilterChange,
		canOpenHubManagement
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [sorting, setSorting] = useState<DataSourceSortableState<EventPreviewModel>>(
		ensureNotNil(
			sortOptions.find(x =>
				x.value.direction === DataSourceSortDirection.Ascending
				&& x.value.propertyName === "startDate"
			)
		)
			?.value
	);

	const [isNewFilter, setIsNewFilter] = useState<HubItemIsNewFilter>("all");

	const [dateFilter, setDateFilter] = useState<EventDateFilterType>(
		ensureNotNil(
			dateFilterOptions.find(x =>
				x.value === EventDateFilterType.Upcoming
			)
		)
			?.value
	);

	const navigateToOverviewHandler = useCallback(
		(categoryId?: number | null): void => {
			onViewChange({ kind: "overview" });

			if (categoryId !== undefined) {
				onSelectCategory(categoryId);
			}
		},
		[onSelectCategory, onViewChange]
	);

	const activeFilterChangedHandler = useCallback(
		(filter: ActiveFilter | null): void => {
			onActiveFilterChange(filter);

			if (filter !== null) {
				navigateToOverviewHandler(null);
			}
		},
		[navigateToOverviewHandler, onActiveFilterChange]
	);

	const getCategory = useCallback(
		(categoryId: number | nil): HubItemCategoryModel | undefined => {
			if (categories === null || isNil(categoryId)) {
				return undefined;
			}

			return categories.find(c => c.id === categoryId);
		},
		[categories]
	);

	const openDetailsHandler = useCallback(
		(id: number): void => {
			onViewChange({
				kind: "details",
				id
			});
		},
		[onViewChange]
	);

	switch (view.kind) {
		case "overview":
			return (
				<Overview
					activeFilter={activeFilter}
					onActiveFilterChange={activeFilterChangedHandler}
					selectedCategoryId={selectedCategoryId}
					onSelectedCategoryIdChange={onSelectCategory}
					categories={categories}
					getCategory={getCategory}
					onCategoryCreated={onCategoryCreated}
					onNavigateToItem={openDetailsHandler}
					searchTerm={searchTerm}
					onSearchTermChange={setSearchTerm}
					sorting={sorting}
					dateFilter={dateFilter}
					onDateSortingChange={setSorting}
					onDateFilterChange={setDateFilter}
					hubItemKindsCanCreateNewCategoriesFor={hubItemKindsCanCreateNewCategoriesFor}
					canOpenHubManagement={canOpenHubManagement}
					isNewFilter={isNewFilter}
					onIsNewFilterChange={setIsNewFilter}
				/>
			);
		case "details":
			return (
				<Details
					mode="full"
					id={view.id}
					selectedCategoryId={selectedCategoryId}
					onSelectCategory={navigateToOverviewHandler}
					activeFilter={activeFilter}
					onActiveFilterChange={activeFilterChangedHandler}
					searchTerm={searchTerm}
					sorting={sorting}
					dateFilter={dateFilter}
					getCategory={getCategory}
					onBack={navigateToOverviewHandler}
					onNavigateToItem={openDetailsHandler}
					onCategoryCreated={onCategoryCreated}
					viewLocation={HubItemViewLocation.Hub}
					isNewFilter={isNewFilter}
					deepLink={view.commentId
						? {
							kind: "comment",
							commentId: view.commentId
						}
						: undefined
					}
				/>
			);
		default:
			never(view);
	}
};
EventsPageContent.displayName = "EventsPageContent";